import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  CIRCLE_OPTIONS,
  TOWER_CM_ZONE_KPIS,
  TOWER_CM_SCORE_HEATMAP,
  TOWER_CM_OVERVIEW_CHARTS,
  TOWER_CM_KPI_DRILLS,
  TOWER_CM_NWA_DIST,
  TOWER_CM_DG_DATA,
  TOWER_CM_OUTAGE_SLAB,
  TOWER_CM_WORST_NWA,
  TOWER_CM_REPEAT_OFFENDERS,
  TOWER_CM_CONSECUTIVE_WO,
  TOWER_CM_INSIGHTS,
  TOWER_CM_INSIGHT_CHIPS,
  TOWER_CM_ACTIONS,
  TOWER_CM_RECOVERABLE_BANNER,
  TOWER_CM_CIRCLE_COLORS,
} from '../../data/towerCmData';
import {
  KpiCard,
  SectionTitle,
  ChipStrip,
  SearchField,
  InsightCard,
  PriorityBadge,
  SortButton,
  EmptyState,
  PeriodUnavailableState,
  ScoreCell,
  palette,
  chartBaseOptions,
  InsightBanner,
} from '../dashboardPrimitives';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

/* ------------------------------------------------------------------ */
/*  Reusable KPI Drill Header                                         */
/* ------------------------------------------------------------------ */
function KpiDrillHeader({ number, title, target, score, scoreOk }) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-xl px-5 py-3"
      style={{ background: 'linear-gradient(90deg, #1e3a5f, #2d5986)' }}
    >
      <span className="rounded-lg bg-red-500 px-2.5 py-1 text-[13px] font-extrabold text-white">
        {number}
      </span>
      <span className="flex-1 text-[14px] font-bold text-white">{title}</span>
      <span className="rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold text-white/90">
        {target}
      </span>
      <span
        className={`rounded-full px-3 py-1 text-[11px] font-bold text-white ${scoreOk ? 'bg-green-500/35' : 'bg-red-500/40'}`}
      >
        {score}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */

function sortValue(row, key) {
  switch (key) {
    case 'cmp':
    case 'circle':
    case 'siteId':
    case 'slab':
      return row[key] ?? '';
    default:
      return row[key] ?? 0;
  }
}

function toggleSort(prev, key) {
  return {
    key,
    dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
  };
}

function applySortAndSearch(rows, sort, search, searchKeys) {
  let result = rows;
  if (search.trim()) {
    const q = search.toLowerCase();
    result = result.filter((r) => searchKeys.some((k) => String(r[k] ?? '').toLowerCase().includes(q)));
  }
  if (sort.key) {
    result = [...result].sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (typeof av === 'string') return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
  }
  return result;
}

/* ------------------------------------------------------------------ */
/*  Color Legend data for heatmap                                      */
/* ------------------------------------------------------------------ */
const SCORE_LEGEND = [
  { label: '0', cls: 'score-cell-0' },
  { label: '5', cls: 'score-cell-5' },
  { label: '10', cls: 'score-cell-10' },
  { label: '15', cls: 'score-cell-15' },
  { label: '20', cls: 'score-cell-20' },
  { label: '30', cls: 'score-cell-30' },
  { label: '40+', cls: 'score-cell-40' },
];

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
export default function TowerCM({ selectedMonth }) {
  /* --- state --- */
  const [activeCircle, setActiveCircle] = useState('ALL');
  const [heatmapSort, setHeatmapSort] = useState({ key: 'finalScore', dir: 'desc' });
  const [heatmapSearch, setHeatmapSearch] = useState('');
  const [nwaSort, setNwaSort] = useState({ key: 'nwa', dir: 'asc' });
  const [nwaSearch, setNwaSearch] = useState('');
  const [repeatSort, setRepeatSort] = useState({ key: 'dc', dir: 'desc' });
  const [repeatSearch, setRepeatSearch] = useState('');
  const [woSearch, setWoSearch] = useState('');
  const [woSort, setWoSort] = useState({ key: 'cmp', dir: 'asc' });

  /* --- filtered heatmap rows --- */
  const filteredHeatmap = useMemo(() => {
    return TOWER_CM_SCORE_HEATMAP.filter(
      (r) => activeCircle === 'ALL' || r.circle === activeCircle,
    );
  }, [activeCircle]);

  const heatmapRows = useMemo(
    () => applySortAndSearch(filteredHeatmap, heatmapSort, heatmapSearch, ['cmp', 'circle']),
    [filteredHeatmap, heatmapSort, heatmapSearch],
  );

  /* --- computed KPI zone summary (from filtered data) --- */
  const zoneKpis = useMemo(() => {
    const rows = filteredHeatmap;
    if (!rows.length) return TOWER_CM_ZONE_KPIS;
    const avg = (arr, k) => arr.reduce((s, r) => s + r[k], 0) / arr.length;
    const avgScore = avg(rows, 'finalScore').toFixed(1);
    const best = [...rows].sort((a, b) => b.finalScore - a.finalScore)[0];
    const worst = [...rows].sort((a, b) => a.finalScore - b.finalScore)[0];
    const avgDg = avg(rows, 'dgStart').toFixed(1);
    const avgSa = avg(rows, 'saAlarms').toFixed(1);
    const nwaMin = Math.min(...rows.map((r) => r.nwa)).toFixed(2);
    const nwaMax = Math.max(...rows.map((r) => r.nwa)).toFixed(2);
    const totalScope = rows.reduce((s, r) => s + r.scope, 0);
    const duSites = rows.reduce((s, r) => s + (TOWER_CM_OUTAGE_SLAB[r.cmp]?.gt4 ?? 0), 0);
    const worstDg = [...rows].sort((a, b) => a.dgStart - b.dgStart)[0];

    return [
      { label: 'Zone Avg Score /60', value: avgScore, sub: `${best.cmp} best: ${best.finalScore} | ${worst.cmp} worst: ${worst.finalScore}`, dot: 'red' },
      { label: 'Penalty % (All CMPs)', value: '7.5%', sub: `All ${rows.length} CMPs in penalty tier`, dot: 'red' },
      { label: `NWA Range (Target ≥99.99%)`, value: `${nwaMin}-${nwaMax}%`, sub: `${totalScope.toLocaleString()} sites in scope`, dot: 'orange' },
      { label: 'DG Start Rate Avg (Target 100%)', value: `${avgDg}%`, sub: `${worstDg.cmp} worst: ${worstDg.dgStart.toFixed(1)}%`, dot: 'red' },
      { label: 'SA Alarm Closure Avg (Target 100%)', value: `${avgSa}%`, sub: `All CMPs below 85% threshold`, dot: 'red' },
      { label: 'Sites: Outage >4× (DU KPI Fail)', value: String(duSites), sub: `${rows.length} CMPs evaluated`, dot: 'orange' },
      TOWER_CM_ZONE_KPIS[6],
      TOWER_CM_ZONE_KPIS[7],
    ];
  }, [filteredHeatmap]);

  const dotColor = (d) => {
    if (d === 'red') return palette.danger;
    if (d === 'orange') return palette.orange;
    if (d === 'green') return palette.success;
    return palette.accent;
  };

  /* --- Overview chart data --- */
  const finalScoreChartData = useMemo(() => {
    const rows = filteredHeatmap;
    return {
      labels: rows.map((r) => r.cmp),
      datasets: [
        {
          label: 'Final Score',
          data: rows.map((r) => r.finalScore),
          backgroundColor: rows.map((r) =>
            r.finalScore >= 30 ? palette.success : r.finalScore >= 15 ? palette.warning : palette.danger,
          ),
          borderRadius: 4,
        },
      ],
    };
  }, [filteredHeatmap]);

  const stackedKpiData = useMemo(() => {
    const rows = filteredHeatmap;
    const defs = TOWER_CM_OVERVIEW_CHARTS.stackedKpiBreakdown.datasets;
    return {
      labels: rows.map((r) => r.cmp),
      datasets: defs.map((d) => ({
        label: d.label,
        data: rows.map((r) => r[d.key]),
        backgroundColor: d.color,
        borderRadius: 2,
      })),
    };
  }, [filteredHeatmap]);

  /* --- NWA drill data --- */
  const filteredNwa = useMemo(() => {
    const rows = TOWER_CM_WORST_NWA.filter(
      (r) => activeCircle === 'ALL' || r.circle === activeCircle,
    );
    return applySortAndSearch(rows, nwaSort, nwaSearch, ['cmp', 'circle', 'siteId']);
  }, [activeCircle, nwaSort, nwaSearch]);

  /* --- Repeat offender data --- */
  const filteredRepeat = useMemo(() => {
    const rows = TOWER_CM_REPEAT_OFFENDERS.filter(
      (r) => activeCircle === 'ALL' || r.circle === activeCircle,
    );
    return applySortAndSearch(rows, repeatSort, repeatSearch, ['cmp', 'circle', 'siteId']);
  }, [activeCircle, repeatSort, repeatSearch]);

  /* --- Consecutive WO data --- */
  const filteredWo = useMemo(() => {
    const rows = TOWER_CM_CONSECUTIVE_WO.sites.filter(
      (r) => activeCircle === 'ALL' || r.circle === activeCircle,
    );
    return applySortAndSearch(rows, woSort, woSearch, ['cmp', 'circle', 'siteId', 'facId']);
  }, [activeCircle, woSort, woSearch]);

  /* --- NWA tier chart data --- */
  const nwaTierChartData = useMemo(() => {
    const cmps = filteredHeatmap.map((r) => r.cmp);
    const defs = TOWER_CM_KPI_DRILLS.kpi41.charts.nwaTierChart.datasets;
    return {
      labels: cmps,
      datasets: defs.map((d) => ({
        label: d.label,
        data: cmps.map((c) => TOWER_CM_NWA_DIST[c]?.[d.key] ?? 0),
        backgroundColor: d.color,
        borderRadius: 2,
      })),
    };
  }, [filteredHeatmap]);

  /* --- Infra proxy chart data --- */
  const infraProxyData = useMemo(() => {
    const cmps = filteredHeatmap.map((r) => r.cmp);
    const defs = TOWER_CM_KPI_DRILLS.kpi44.charts.infraProxyChart.datasets;
    return {
      labels: cmps,
      datasets: defs.map((d) => ({
        label: d.label,
        data: cmps.map((c) => TOWER_CM_DG_DATA[c]?.[d.key] ?? 0),
        backgroundColor: d.color,
        borderRadius: 2,
      })),
    };
  }, [filteredHeatmap]);

  /* --- DG monitoring chart --- */
  const dgMonData = useMemo(() => {
    const cmps = filteredHeatmap.map((r) => r.cmp);
    return {
      labels: cmps,
      datasets: [
        {
          label: 'DG Not Visible (NMS)',
          data: cmps.map((c) => TOWER_CM_DG_DATA[c]?.dgOffline ?? 0),
          backgroundColor: '#ef444480',
          borderRadius: 4,
        },
      ],
    };
  }, [filteredHeatmap]);

  /* --- Outage slab chart data --- */
  const outageSlabData = useMemo(() => {
    const cmps = filteredHeatmap.map((r) => r.cmp);
    const defs = TOWER_CM_KPI_DRILLS.kpi46.charts.outageSlabChart.datasets;
    return {
      labels: cmps,
      datasets: defs.map((d) => ({
        label: d.label,
        data: cmps.map((c) => TOWER_CM_OUTAGE_SLAB[c]?.[d.key] ?? 0),
        backgroundColor: d.color,
        borderRadius: 2,
      })),
    };
  }, [filteredHeatmap]);

  /* --- bar chart helper --- */
  const simpleBarOptions = (yLabel) => ({
    ...chartBaseOptions,
    plugins: {
      ...chartBaseOptions.plugins,
      legend: { display: false },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
      },
      y: {
        grid: { color: palette.border },
        border: { display: false },
        ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } },
      },
    },
  });

  const stackedBarOptions = {
    ...chartBaseOptions,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' }, usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 14 },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } },
      y: { stacked: true, grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
    },
  };

  const groupedBarOptions = {
    ...chartBaseOptions,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' }, usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 14 },
      },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } } },
      y: { grid: { color: palette.border }, border: { display: false }, ticks: { color: palette.slateDeep, font: { family: 'JetBrains Mono', size: 10, weight: '500' } } },
    },
  };

  /* ------------------------------------------------------------------ */
  /*  Empty state guard                                                  */
  /* ------------------------------------------------------------------ */
  if (!filteredHeatmap.length) {
    return (
      <div className="space-y-6">
        <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: CIRCLE_OPTIONS }]} />
        <EmptyState />
      </div>
    );
  }

  if (selectedMonth !== "Feb'26") {
    return <PeriodUnavailableState monthLabel={selectedMonth} />;
  }

  /* ================================================================= */
  /*  RENDER                                                            */
  /* ================================================================= */
  return (
    <div className="space-y-6">

      {/* ============================================================= */}
      {/* 1. FILTER BAR                                                  */}
      {/* ============================================================= */}
      <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: CIRCLE_OPTIONS }]} />

      {/* ============================================================= */}
      {/* 3. ZONE KPI SUMMARY                                           */}
      {/* ============================================================= */}
      <SectionTitle dot={palette.accent}>Zone KPI Summary</SectionTitle>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {zoneKpis.map((k, i) => (
          <KpiCard
            key={i}
            label={k.label}
            value={k.value}
            sub={k.sub}
            dot={dotColor(k.dot)}
            valueColor={dotColor(k.dot)}
          />
        ))}
      </div>

      {/* ============================================================= */}
      {/* 4. 6-KPI SCORE HEATMAP                                        */}
      {/* ============================================================= */}
      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <SectionTitle dot={palette.warning}>6-KPI Score Heatmap</SectionTitle>
            <p className="dashboard-note mb-0">
              {filteredHeatmap.length} CMPs &times; 6 KPI parameters. Scores colour-coded by tier.
            </p>
          </div>
          <SearchField value={heatmapSearch} onChange={setHeatmapSearch} placeholder="Search CMPs..." className="sm:max-w-xs" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-slate-500">
          {SCORE_LEGEND.map((s) => (
            <span key={s.label} className="flex items-center gap-1.5">
              <ScoreCell value={s.label} className="text-[10px]" /> {s.label === '0' ? 'Zero' : s.label === '40+' ? '40+' : s.label}
            </span>
          ))}
        </div>
        {heatmapRows.length ? (
          <div className="mt-4 table-scroll-shell">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th><SortButton label="CMP" sortKey="cmp" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Circle" sortKey="circle" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Scope" sortKey="scope" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} /></th>
                  <th className="text-center">
                    <SortButton label="4.1 NWA" sortKey="nwaScore" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} />
                    <div className="mt-0.5 text-[9px] font-normal tracking-normal text-slate-400">max 30</div>
                  </th>
                  <th className="text-center">
                    <SortButton label="4.4 Infra" sortKey="infraScore" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} />
                    <div className="mt-0.5 text-[9px] font-normal tracking-normal text-slate-400">max 20</div>
                  </th>
                  <th className="text-center">
                    <SortButton label="4.2 SA Alarms" sortKey="saScore" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} />
                    <div className="mt-0.5 text-[9px] font-normal tracking-normal text-slate-400">max 10</div>
                  </th>
                  <th className="text-center">
                    <SortButton label="4.3 Non-SA" sortKey="nsaScore" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} />
                    <div className="mt-0.5 text-[9px] font-normal tracking-normal text-slate-400">max 10</div>
                  </th>
                  <th className="text-center">
                    <SortButton label="4.5 DG Start" sortKey="dgScore" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} />
                    <div className="mt-0.5 text-[9px] font-normal tracking-normal text-slate-400">max 15</div>
                  </th>
                  <th className="text-center">
                    <SortButton label="4.6 DU Cell Down" sortKey="duScore" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} />
                    <div className="mt-0.5 text-[9px] font-normal tracking-normal text-slate-400">max 10</div>
                  </th>
                  <th><SortButton label="Final Score" sortKey="finalScore" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Penalty %" sortKey="penalty" sortState={heatmapSort} onSort={(k) => setHeatmapSort((p) => toggleSort(p, k))} /></th>
                </tr>
              </thead>
              <tbody>
                {heatmapRows.map((r) => (
                  <tr key={r.cmp}>
                    <td className="font-semibold text-slate-700">{r.cmp}</td>
                    <td>
                      <span
                        className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                        style={{ backgroundColor: `${TOWER_CM_CIRCLE_COLORS[r.circle]}16`, color: TOWER_CM_CIRCLE_COLORS[r.circle] }}
                      >
                        {r.circle}
                      </span>
                    </td>
                    <td className="mono-cell">{r.scope.toLocaleString()}</td>
                    <td className="text-center"><ScoreCell value={r.nwaScore} /></td>
                    <td className="text-center"><ScoreCell value={r.infraScore} /></td>
                    <td className="text-center"><ScoreCell value={r.saScore} /></td>
                    <td className="text-center"><ScoreCell value={r.nsaScore} /></td>
                    <td className="text-center"><ScoreCell value={r.dgScore} /></td>
                    <td className="text-center"><ScoreCell value={r.duScore} /></td>
                    <td>
                      <span
                        className="mono-cell inline-flex rounded-lg px-2 py-1 text-[10px] font-semibold text-white"
                        style={{
                          backgroundColor: r.finalScore >= 30 ? palette.success : r.finalScore >= 15 ? palette.warning : palette.danger,
                        }}
                      >
                        {r.finalScore}
                      </span>
                    </td>
                    <td className="mono-cell font-semibold text-red-500">{r.penalty}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="mt-4 min-h-[220px]" />
        )}
      </div>

      {/* ============================================================= */}
      {/* 5. ZONE OVERVIEW CHARTS                                        */}
      {/* ============================================================= */}
      <SectionTitle dot={palette.navy}>Zone Overview Charts</SectionTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>{TOWER_CM_OVERVIEW_CHARTS.finalScore.title}</SectionTitle>
          <div style={{ height: 280 }}>
            <Bar data={finalScoreChartData} options={simpleBarOptions()} />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.navy}>{TOWER_CM_OVERVIEW_CHARTS.stackedKpiBreakdown.title}</SectionTitle>
          <div style={{ height: 280 }}>
            <Bar data={stackedKpiData} options={stackedBarOptions} />
          </div>
        </div>
      </div>

      {/* ============================================================= */}
      {/* 6. KPI 4.1 - eNodeB Availability (NWA)                        */}
      {/* ============================================================= */}
      <KpiDrillHeader
        number={TOWER_CM_KPI_DRILLS.kpi41.number}
        title={TOWER_CM_KPI_DRILLS.kpi41.title}
        target={TOWER_CM_KPI_DRILLS.kpi41.target}
        score={TOWER_CM_KPI_DRILLS.kpi41.chips[1]}
        scoreOk={false}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>CMP Availability vs Target</SectionTitle>
          <div style={{ height: 260 }}>
            <Bar
              data={{
                labels: filteredHeatmap.map((r) => r.cmp),
                datasets: [
                  {
                    label: 'NWA %',
                    data: filteredHeatmap.map((r) => r.nwa),
                    backgroundColor: filteredHeatmap.map((r) =>
                      r.nwa >= 99.85 ? '#22c55e80' : '#ef444480',
                    ),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...simpleBarOptions(),
                scales: {
                  ...simpleBarOptions().scales,
                  y: {
                    ...simpleBarOptions().scales.y,
                    min: 97.5,
                    max: 100.1,
                  },
                },
              }}
            />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.warning}>NWA Tier Distribution</SectionTitle>
          <div style={{ height: 260 }}>
            <Bar data={nwaTierChartData} options={stackedBarOptions} />
          </div>
        </div>
      </div>
      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <SectionTitle dot={palette.danger}>Worst NWA Sites (NWA &lt; 99%)</SectionTitle>
          <SearchField value={nwaSearch} onChange={setNwaSearch} placeholder="Search sites..." className="sm:max-w-xs" />
        </div>
        {filteredNwa.length ? (
          <div className="mt-4 table-scroll-shell">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th><SortButton label="CMP" sortKey="cmp" sortState={nwaSort} onSort={(k) => setNwaSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Circle" sortKey="circle" sortState={nwaSort} onSort={(k) => setNwaSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Site ID" sortKey="siteId" sortState={nwaSort} onSort={(k) => setNwaSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="NWA %" sortKey="nwa" sortState={nwaSort} onSort={(k) => setNwaSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Down Count" sortKey="dc" sortState={nwaSort} onSort={(k) => setNwaSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Outage Slab" sortKey="slab" sortState={nwaSort} onSort={(k) => setNwaSort((p) => toggleSort(p, k))} /></th>
                </tr>
              </thead>
              <tbody>
                {filteredNwa.map((r) => (
                  <tr key={r.siteId}>
                    <td className="font-semibold text-slate-700">{r.cmp}</td>
                    <td>
                      <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: `${TOWER_CM_CIRCLE_COLORS[r.circle]}16`, color: TOWER_CM_CIRCLE_COLORS[r.circle] }}>
                        {r.circle}
                      </span>
                    </td>
                    <td className="mono-cell">{r.siteId}</td>
                    <td className="mono-cell" style={{ color: r.nwa < 90 ? palette.danger : r.nwa < 99 ? palette.warning : palette.success }}>{r.nwa.toFixed(3)}%</td>
                    <td className="mono-cell">{r.dc}</td>
                    <td>{r.slab || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="mt-4 min-h-[180px]" />
        )}
      </div>

      {/* ============================================================= */}
      {/* 7. KPI 4.2 - SA Alarms                                        */}
      {/* ============================================================= */}
      <KpiDrillHeader
        number={TOWER_CM_KPI_DRILLS.kpi42.number}
        title={TOWER_CM_KPI_DRILLS.kpi42.title}
        target={TOWER_CM_KPI_DRILLS.kpi42.target}
        score={TOWER_CM_KPI_DRILLS.kpi42.chips[0]}
        scoreOk={false}
      />
      {TOWER_CM_KPI_DRILLS.kpi42.dataNotes && (
        <p className="dashboard-note">{TOWER_CM_KPI_DRILLS.kpi42.dataNotes}</p>
      )}
      <div className="card-surface p-6">
        <SectionTitle dot={palette.orange}>SA Alarm Closure % by CMP</SectionTitle>
        <div style={{ height: 260 }}>
          <Bar
            data={{
              labels: filteredHeatmap.map((r) => r.cmp),
              datasets: [
                {
                  label: 'SA Alarm Closure %',
                  data: filteredHeatmap.map((r) => r.saAlarms),
                  backgroundColor: filteredHeatmap.map((r) =>
                    r.saAlarms >= 85 ? '#22c55e80' : '#ef444480',
                  ),
                  borderRadius: 4,
                },
              ],
            }}
            options={{
              ...simpleBarOptions(),
              scales: {
                ...simpleBarOptions().scales,
                y: { ...simpleBarOptions().scales.y, min: 60, max: 105 },
              },
            }}
          />
        </div>
      </div>
      <div className="card-surface p-6">
        <SectionTitle dot={palette.danger}>SA Alarm Gap Analysis</SectionTitle>
        <div className="mt-4 table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>CMP</th>
                <th>Circle</th>
                <th>SA Closure %</th>
                <th>Gap to 85%</th>
                <th>Points if 85%</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredHeatmap.map((r) => {
                const gap = Math.max(0, 85 - r.saAlarms).toFixed(1);
                return (
                  <tr key={`sa-${r.cmp}`}>
                    <td className="font-semibold text-slate-700">{r.cmp}</td>
                    <td>
                      <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: `${TOWER_CM_CIRCLE_COLORS[r.circle]}16`, color: TOWER_CM_CIRCLE_COLORS[r.circle] }}>
                        {r.circle}
                      </span>
                    </td>
                    <td className="mono-cell">{r.saAlarms}%</td>
                    <td className="mono-cell" style={{ color: Number(gap) > 10 ? palette.danger : palette.warning }}>{gap}%</td>
                    <td className="mono-cell font-semibold text-blue-600">+5 pts</td>
                    <td>{r.saAlarms >= 85 ? <span className="text-emerald-600 font-semibold">At Threshold</span> : <span className="text-red-500 font-semibold">Below</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================================= */}
      {/* 8. KPI 4.3 - Non-SA Alarms                                    */}
      {/* ============================================================= */}
      <KpiDrillHeader
        number={TOWER_CM_KPI_DRILLS.kpi43.number}
        title={TOWER_CM_KPI_DRILLS.kpi43.title}
        target={TOWER_CM_KPI_DRILLS.kpi43.target}
        score={TOWER_CM_KPI_DRILLS.kpi43.chips[0]}
        scoreOk={TOWER_CM_KPI_DRILLS.kpi43.chipTone === 'ok'}
      />
      {TOWER_CM_KPI_DRILLS.kpi43.dataNotes && (
        <p className="dashboard-note">{TOWER_CM_KPI_DRILLS.kpi43.dataNotes}</p>
      )}
      <div className="card-surface p-6">
        <SectionTitle dot={palette.success}>Non-SA Alarm Closure % by CMP</SectionTitle>
        <div style={{ height: 260 }}>
          <Bar
            data={{
              labels: filteredHeatmap.map((r) => r.cmp),
              datasets: [
                {
                  label: 'Non-SA Closure %',
                  data: filteredHeatmap.map((r) => r.nsaAlarms),
                  backgroundColor: filteredHeatmap.map((r) =>
                    r.nsaAlarms >= 99 ? '#22c55e80' : r.nsaAlarms >= 85 ? '#eab30880' : '#ef444480',
                  ),
                  borderRadius: 4,
                },
              ],
            }}
            options={{
              ...simpleBarOptions(),
              scales: {
                ...simpleBarOptions().scales,
                y: { ...simpleBarOptions().scales.y, min: 80, max: 101 },
              },
            }}
          />
        </div>
      </div>
      <div className="card-surface p-6">
        <SectionTitle dot={palette.accent}>Non-SA Alarm Score Breakdown</SectionTitle>
        <div className="mt-4 table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>CMP</th>
                <th>Circle</th>
                <th>Non-SA %</th>
                <th>Score Earned</th>
                <th>Max Possible</th>
                <th>Points Left</th>
              </tr>
            </thead>
            <tbody>
              {filteredHeatmap.map((r) => (
                <tr key={`nsa-${r.cmp}`}>
                  <td className="font-semibold text-slate-700">{r.cmp}</td>
                  <td>
                    <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: `${TOWER_CM_CIRCLE_COLORS[r.circle]}16`, color: TOWER_CM_CIRCLE_COLORS[r.circle] }}>
                      {r.circle}
                    </span>
                  </td>
                  <td className="mono-cell">{r.nsaAlarms}%</td>
                  <td className="mono-cell font-semibold" style={{ color: r.nsaScore >= 10 ? palette.success : r.nsaScore >= 5 ? palette.warning : palette.danger }}>{r.nsaScore}</td>
                  <td className="mono-cell">10</td>
                  <td className="mono-cell font-semibold text-blue-600">{10 - r.nsaScore}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================================= */}
      {/* 9. KPI 4.4 - Infrastructure Uptime                            */}
      {/* ============================================================= */}
      <KpiDrillHeader
        number={TOWER_CM_KPI_DRILLS.kpi44.number}
        title={TOWER_CM_KPI_DRILLS.kpi44.title}
        target={TOWER_CM_KPI_DRILLS.kpi44.target}
        score={TOWER_CM_KPI_DRILLS.kpi44.chips[0]}
        scoreOk={false}
      />
      {TOWER_CM_KPI_DRILLS.kpi44.dataNotes && (
        <p className="dashboard-note">{TOWER_CM_KPI_DRILLS.kpi44.dataNotes}</p>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.success}>Infra Uptime % by CMP vs 99.95% Threshold</SectionTitle>
          <div style={{ height: 260 }}>
            <Bar
              data={{
                labels: filteredHeatmap.map((r) => r.cmp),
                datasets: [
                  {
                    label: 'Infra Uptime %',
                    data: filteredHeatmap.map((r) => r.infra),
                    backgroundColor: filteredHeatmap.map((r) =>
                      r.infra >= 99.95 ? '#22c55e80' : '#f9731680',
                    ),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...simpleBarOptions(),
                scales: {
                  ...simpleBarOptions().scales,
                  y: { ...simpleBarOptions().scales.y, min: 99.5, max: 100.05 },
                },
              }}
            />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.orange}>Infra Monitoring Health per CMP</SectionTitle>
          <div style={{ height: 260 }}>
            <Bar data={infraProxyData} options={groupedBarOptions} />
          </div>
        </div>
      </div>
      <div className="card-surface p-6">
        <SectionTitle dot={palette.navy}>Infrastructure Root-Cause Table</SectionTitle>
        <div className="mt-4 table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>CMP</th>
                <th>Circle</th>
                <th>Scope</th>
                <th>SMPS Offline</th>
                <th>DG Not Visible</th>
                <th>Battery Gap</th>
                <th>Fuel Faulty</th>
                <th>Invalid KPI</th>
                <th>Infra Score</th>
              </tr>
            </thead>
            <tbody>
              {filteredHeatmap.map((r) => {
                const d = TOWER_CM_DG_DATA[r.cmp] ?? {};
                return (
                  <tr key={`infra-${r.cmp}`}>
                    <td className="font-semibold text-slate-700">{r.cmp}</td>
                    <td>
                      <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: `${TOWER_CM_CIRCLE_COLORS[r.circle]}16`, color: TOWER_CM_CIRCLE_COLORS[r.circle] }}>
                        {r.circle}
                      </span>
                    </td>
                    <td className="mono-cell">{r.scope.toLocaleString()}</td>
                    <td className="mono-cell">{d.smpsOffline ?? '-'}</td>
                    <td className="mono-cell">{d.dgOffline ?? '-'}</td>
                    <td className="mono-cell">{d.battGap ?? '-'}</td>
                    <td className="mono-cell">{d.fuelFaulty ?? '-'}</td>
                    <td className="mono-cell">{d.invalidKpi ?? '-'}</td>
                    <td><ScoreCell value={r.infraScore} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================================= */}
      {/* 10. KPI 4.5 - DG Start Failure                                */}
      {/* ============================================================= */}
      <KpiDrillHeader
        number={TOWER_CM_KPI_DRILLS.kpi45.number}
        title={TOWER_CM_KPI_DRILLS.kpi45.title}
        target={TOWER_CM_KPI_DRILLS.kpi45.target}
        score={TOWER_CM_KPI_DRILLS.kpi45.chips[0]}
        scoreOk={false}
      />
      {TOWER_CM_KPI_DRILLS.kpi45.dataNotes && (
        <p className="dashboard-note">{TOWER_CM_KPI_DRILLS.kpi45.dataNotes}</p>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.danger}>DG Start Rate % by CMP (Target 100%)</SectionTitle>
          <div style={{ height: 260 }}>
            <Bar
              data={{
                labels: filteredHeatmap.map((r) => r.cmp),
                datasets: [
                  {
                    label: 'DG Start %',
                    data: filteredHeatmap.map((r) => r.dgStart),
                    backgroundColor: filteredHeatmap.map((r) =>
                      r.dgStart >= 85 ? '#22c55e80' : r.dgStart >= 70 ? '#eab30880' : '#ef444480',
                    ),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...simpleBarOptions(),
                scales: {
                  ...simpleBarOptions().scales,
                  y: { ...simpleBarOptions().scales.y, min: 40, max: 105 },
                },
              }}
            />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.orange}>DG Monitoring Failures by CMP</SectionTitle>
          <div style={{ height: 260 }}>
            <Bar data={dgMonData} options={simpleBarOptions()} />
          </div>
        </div>
      </div>
      <div className="card-surface p-6">
        <SectionTitle dot={palette.danger}>DG Risk Register</SectionTitle>
        <div className="mt-4 table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>CMP</th>
                <th>Circle</th>
                <th>DG Start %</th>
                <th>Gap to 85%</th>
                <th>DG Offline (NMS)</th>
                <th>Fuel Sensor Faulty</th>
                <th>Risk Level</th>
                <th>DG Score</th>
              </tr>
            </thead>
            <tbody>
              {filteredHeatmap.map((r) => {
                const d = TOWER_CM_DG_DATA[r.cmp] ?? {};
                const gap = Math.max(0, 85 - r.dgStart).toFixed(1);
                const risk = r.dgStart < 60 ? 'Critical' : r.dgStart < 70 ? 'High' : r.dgStart < 85 ? 'Medium' : 'Low';
                const riskColor = risk === 'Critical' ? palette.danger : risk === 'High' ? palette.orange : risk === 'Medium' ? palette.warning : palette.success;
                return (
                  <tr key={`dg-${r.cmp}`}>
                    <td className="font-semibold text-slate-700">{r.cmp}</td>
                    <td>
                      <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: `${TOWER_CM_CIRCLE_COLORS[r.circle]}16`, color: TOWER_CM_CIRCLE_COLORS[r.circle] }}>
                        {r.circle}
                      </span>
                    </td>
                    <td className="mono-cell">{r.dgStart.toFixed(1)}%</td>
                    <td className="mono-cell" style={{ color: Number(gap) > 15 ? palette.danger : palette.warning }}>{gap}%</td>
                    <td className="mono-cell">{d.dgOffline ?? '-'}</td>
                    <td className="mono-cell">{d.fuelFaulty ?? '-'}</td>
                    <td>
                      <span className="inline-flex rounded-lg px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: `${riskColor}18`, color: riskColor }}>
                        {risk}
                      </span>
                    </td>
                    <td><ScoreCell value={r.dgScore} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================================= */}
      {/* 11. KPI 4.6 - DU Cell Down                                    */}
      {/* ============================================================= */}
      <KpiDrillHeader
        number={TOWER_CM_KPI_DRILLS.kpi46.number}
        title={TOWER_CM_KPI_DRILLS.kpi46.title}
        target={TOWER_CM_KPI_DRILLS.kpi46.target}
        score={TOWER_CM_KPI_DRILLS.kpi46.chips[0]}
        scoreOk={TOWER_CM_KPI_DRILLS.kpi46.chipTone === 'ok'}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>DU Cell Down Rate % by CMP vs 99.98% Threshold</SectionTitle>
          <div style={{ height: 260 }}>
            <Bar
              data={{
                labels: filteredHeatmap.map((r) => r.cmp),
                datasets: [
                  {
                    label: 'DU Cell Down %',
                    data: filteredHeatmap.map((r) => r.duCellDown),
                    backgroundColor: filteredHeatmap.map((r) =>
                      r.duCellDown >= 99.99 ? '#22c55e80' : r.duCellDown >= 99.98 ? '#3b82f680' : '#f9731680',
                    ),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...simpleBarOptions(),
                scales: {
                  ...simpleBarOptions().scales,
                  y: { ...simpleBarOptions().scales.y, min: 99.75, max: 100.01 },
                },
              }}
            />
          </div>
        </div>
        <div className="card-surface p-6">
          <SectionTitle dot={palette.danger}>Sites by Outage Slab per CMP</SectionTitle>
          <div style={{ height: 260 }}>
            <Bar data={outageSlabData} options={stackedBarOptions} />
          </div>
        </div>
      </div>
      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <SectionTitle dot={palette.danger}>Repeat Offender Sites (Down Count &ge; 10)</SectionTitle>
          <SearchField value={repeatSearch} onChange={setRepeatSearch} placeholder="Search sites..." className="sm:max-w-xs" />
        </div>
        {filteredRepeat.length ? (
          <div className="mt-4 table-scroll-shell">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th><SortButton label="CMP" sortKey="cmp" sortState={repeatSort} onSort={(k) => setRepeatSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Circle" sortKey="circle" sortState={repeatSort} onSort={(k) => setRepeatSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Site ID" sortKey="siteId" sortState={repeatSort} onSort={(k) => setRepeatSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Down Count" sortKey="dc" sortState={repeatSort} onSort={(k) => setRepeatSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="NWA %" sortKey="nwa" sortState={repeatSort} onSort={(k) => setRepeatSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Outage Slab" sortKey="slab" sortState={repeatSort} onSort={(k) => setRepeatSort((p) => toggleSort(p, k))} /></th>
                </tr>
              </thead>
              <tbody>
                {filteredRepeat.map((r) => (
                  <tr key={r.siteId}>
                    <td className="font-semibold text-slate-700">{r.cmp}</td>
                    <td>
                      <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: `${TOWER_CM_CIRCLE_COLORS[r.circle]}16`, color: TOWER_CM_CIRCLE_COLORS[r.circle] }}>
                        {r.circle}
                      </span>
                    </td>
                    <td className="mono-cell">{r.siteId}</td>
                    <td className="mono-cell font-semibold" style={{ color: r.dc >= 100 ? palette.danger : r.dc >= 50 ? palette.warning : palette.slateDeep }}>{r.dc}</td>
                    <td className="mono-cell" style={{ color: r.nwa < 90 ? palette.danger : r.nwa < 99 ? palette.warning : palette.success }}>{r.nwa.toFixed(3)}%</td>
                    <td>{r.slab || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="mt-4 min-h-[180px]" />
        )}
      </div>

      {/* ============================================================= */}
      {/* 12. 3-CONSECUTIVE PM WO PENDING                               */}
      {/* ============================================================= */}
      <SectionTitle dot={palette.danger}>3-Consecutive PM WO Pending (Triple Penalty Risk)</SectionTitle>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {TOWER_CM_CONSECUTIVE_WO.stats.map((s, i) => (
          <div key={i} className="card-surface p-5 text-center">
            <div className="text-2xl font-extrabold" style={{ color: dotColor(s.color) }}>{s.value}</div>
            <div className="mt-1 text-[12px] font-semibold text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>
      <p className="dashboard-note">{TOWER_CM_CONSECUTIVE_WO.dataNotes}</p>
      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <SectionTitle dot={palette.warning}>Sites at Risk</SectionTitle>
          <SearchField value={woSearch} onChange={setWoSearch} placeholder="Search sites..." className="sm:max-w-xs" />
        </div>
        {filteredWo.length ? (
          <div className="mt-4 table-scroll-shell" style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table className="evidence-table">
              <thead>
                <tr>
                  <th><SortButton label="CMP" sortKey="cmp" sortState={woSort} onSort={(k) => setWoSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Circle" sortKey="circle" sortState={woSort} onSort={(k) => setWoSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Site ID" sortKey="siteId" sortState={woSort} onSort={(k) => setWoSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Facility ID" sortKey="facId" sortState={woSort} onSort={(k) => setWoSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="Site Type" sortKey="stype" sortState={woSort} onSort={(k) => setWoSort((p) => toggleSort(p, k))} /></th>
                  <th><SortButton label="State" sortKey="state" sortState={woSort} onSort={(k) => setWoSort((p) => toggleSort(p, k))} /></th>
                </tr>
              </thead>
              <tbody>
                {filteredWo.map((r) => (
                  <tr key={r.siteId}>
                    <td className="font-semibold text-slate-700">{r.cmp}</td>
                    <td>
                      <span className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold" style={{ backgroundColor: `${TOWER_CM_CIRCLE_COLORS[r.circle]}16`, color: TOWER_CM_CIRCLE_COLORS[r.circle] }}>
                        {r.circle}
                      </span>
                    </td>
                    <td className="mono-cell">{r.siteId}</td>
                    <td className="mono-cell">{r.facId}</td>
                    <td>{r.stype}</td>
                    <td>{r.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="mt-4 min-h-[180px]" />
        )}
      </div>

      {/* ============================================================= */}
      {/* 13. EXECUTIVE INSIGHTS                                        */}
      {/* ============================================================= */}
      <InsightBanner chips={TOWER_CM_INSIGHT_CHIPS} />

      <div className="grid grid-cols-1 gap-4">
        {TOWER_CM_INSIGHTS.map((insight) => (
          <InsightCard
            key={insight.title}
            tone={insight.tone}
            title={insight.title}
            exposure={insight.exposure}
            body={insight.body}
            actionTitle={insight.actionTitle}
            action={insight.action}
          />
        ))}
      </div>

      {/* ============================================================= */}
      {/* 14. PRIORITISED ACTION PLAN                                   */}
      {/* ============================================================= */}
      <div className="card-surface border border-emerald-200 bg-emerald-50/80 p-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-emerald-700">
          Recoverable Upside
        </div>
        <div className="mt-2 text-[13px] font-semibold leading-6 text-emerald-900">
          {TOWER_CM_RECOVERABLE_BANNER.text}
        </div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.success}>Prioritised Action Plan</SectionTitle>
        <div className="table-scroll-shell">
          <table className="action-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Action</th>
                <th>KPI</th>
                <th>CMPs Impacted</th>
                <th>Owner</th>
                <th>Expected Impact</th>
              </tr>
            </thead>
            <tbody>
              {TOWER_CM_ACTIONS.map((row) => (
                <tr key={row.action}>
                  <td><PriorityBadge priority={row.priority} /></td>
                  <td>
                    <div className="font-semibold text-slate-700 whitespace-normal">{row.action}</div>
                  </td>
                  <td className="text-slate-600">{row.kpi}</td>
                  <td className="text-slate-600">{row.cmps}</td>
                  <td className="text-slate-600">{row.owner}</td>
                  <td className="text-[11px] leading-5 text-slate-600">{row.expectedImpact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
